import React, { useState } from "react";
import { Search, Plus, Pencil, ChevronRight, Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AccountStatusBanner } from "../components/AccountStatusBanner";
import { ProfileOverlay } from "../components/ProfileOverlay";
import { useAuth } from "../context/AuthContext";
import { useAccountStatus } from "../hooks/useAccountStatus";
import { useMe } from "../hooks/useMe";
import { useTransferFlow } from "../context/TransferFlowContext";
import { useNotifications } from "../hooks/useNotifications";

export function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { recipients, isLoadingRecipients } = useMe();
  const { setRecipientId } = useTransferFlow();
  const { unreadCount } = useNotifications();
  const { canTransact, notice } = useAccountStatus();
  // Tooltip for controls the account state has disabled, so a greyed-out
  // button explains itself rather than looking broken.
  const restrictionTitle = notice?.body ?? "This is unavailable on your account.";

  const [isProfileOverlayOpen, setIsProfileOverlayOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const firstName = user?.full_name?.split(" ")[0] ?? "there";
  const initials = user?.full_name
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() ?? "?";

  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  const filteredRecipients = (recipients ?? []).filter((r) => {
    const q = searchQuery.toLowerCase();
    return (
      r.full_name?.toLowerCase().includes(q) ||
      r.nickname?.toLowerCase().includes(q) ||
      r.country_code?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-full bg-transparent pb-24 text-left overflow-y-auto scrollbar-hidden">

      {/* HEADER (UPDATED TO ELEGANT DARK GREEN) */}
      <div className="bg-[#042416] md:mx-auto md:max-w-5xl md:mt-6 md:rounded-[40px] rounded-b-[50px] px-6 pt-12 pb-8 shadow-[0_12px_30px_-10px_rgba(4,36,22,0.4)] relative z-10">

        <div className="flex justify-between items-start mb-8">
          <div className="text-[#C0F6B7]">
            <p className="text-[11px] md:text-xs text-white/80 font-medium uppercase tracking-[0.15em] mb-1.5">
              {greeting}
            </p>
            <h1 className="text-[28px] md:text-4xl text-[#C0F6B7] font-serif font-semibold leading-tight">
              {firstName}
            </h1>
            <p className="text-xs md:text-sm text-white/70 mt-2 font-light max-w-[220px]">
              Welcome Back !
            </p>
          </div>

          <div className="flex items-center gap-2.5">
            <button
              onClick={() => navigate("/notifications")}
              className="relative flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-white transition-transform active:scale-95 border border-white/5"
              aria-label={`${unreadCount} unread notifications`}
            >
              <Bell size={18} strokeWidth={1.5} />
              {unreadCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 rounded-full bg-[#FFB800] text-[#042416] text-[10px] font-bold flex items-center justify-center shadow-sm">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setIsProfileOverlayOpen(true)}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#FFB800] text-[#042416] transition-transform active:scale-95"
            >
              <span className="font-serif text-sm font-bold">
                {initials}
              </span>
            </button>
          </div>
        </div>

        {/* PROFILE OVERLAY */}
        {isProfileOverlayOpen && (
          <div
            className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" // Changed items-end to items-center, added p-4
            onClick={() => setIsProfileOverlayOpen(false)}
          >
            <div
              className="w-full max-w-md" // Adjusted width limit if desired (e.g., max-w-md or max-w-lg)
              onClick={(e) => e.stopPropagation()}
            >
              <ProfileOverlay
                onClose={() => setIsProfileOverlayOpen(false)}
                onActivityPress={() => navigate("/transactionhistory")}
                onSupportPress={() => navigate("/support")}
              />
            </div>
          </div>
        )}

        {/* SEARCH */}
        <div className="relative mt-2">
          <Search
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50"
            size={16}
            strokeWidth={1.5}
          />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            type="text"
            placeholder="Search recipients"
            className="w-full bg-white/10 border border-white/5 rounded-[16px] py-3.5 pl-11 pr-4 text-white text-[13px] md:text-[14px] placeholder-white/50 shadow-inner focus:outline-none focus:bg-white/20 transition-all"
          />
        </div>
      </div>

      {/* CONTENT (ORIGINAL STYLING RESTORED EXACTLY) */}
      <div className="max-w-5xl mx-auto px-5 md:px-0">

        {/* An admin suspending or closing an account previously produced no
            visible change here at all - the customer saw a normal dashboard and
            only found out when an action failed with a 403. */}
        <AccountStatusBanner className="mt-6" />

        {/* SECTION HEADER */}
        <div className="pt-6 flex items-center justify-between">
          <h2 className="text-[9px] md:text-[10px] font-semibold uppercase tracking-[0.2em] text-[#0a2540]/40">
            Recipients
          </h2>
          <button
            onClick={() => navigate("/createrecipient")}
            disabled={!canTransact}
            title={canTransact ? "Add a recipient" : restrictionTitle}
            aria-label="Add a recipient"
            className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-[#B9F2A1] border border-[#0A4730] flex items-center justify-center active:scale-95 transition disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100"
          >
            <Plus size={14} className="text-[#0A4730]" />
          </button>
        </div>

        {/* LIST */}
        <div className="py-4 space-y-2.5">

          {/* LOADING SKELETONS */}
          {isLoadingRecipients && (
            [...Array(3)].map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-4 flex items-center gap-3 border border-[#0a2540]/5 shadow-sm animate-pulse"
              >
                <div className="w-10 h-10 rounded-xl bg-slate-100 shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-slate-100 rounded w-1/3" />
                  <div className="h-2.5 bg-slate-100 rounded w-1/4" />
                </div>
              </div>
            ))
          )}

          {/* EMPTY STATE */}
          {!isLoadingRecipients && filteredRecipients.length === 0 && (
            <div className="text-center py-16 space-y-3">
              <p className="text-sm font-medium text-[#0a2540]/40">
                {searchQuery ? "No recipients match your search" : "No recipients yet"}
              </p>
              {!searchQuery && (
                <button
                  onClick={() => navigate("/createrecipient")}
                  className="text-xs font-semibold text-[#0A4730] underline underline-offset-2"
                >
                  Add your first recipient
                </button>
              )}
            </div>
          )}

          {/* RECIPIENT CARDS */}
          {!isLoadingRecipients && filteredRecipients.map((r) => (
            <div
              key={r.id}
              className="bg-white rounded-2xl border border-[#0a2540]/5 shadow-sm flex items-center overflow-hidden"
            >
              {/* MAIN TAP AREA → /transfer */}
              <button
                onClick={() => {
                  setRecipientId(r.id);
                  navigate("/transfer");
                }}
                disabled={!canTransact}
                title={canTransact ? undefined : restrictionTitle}
                className="flex-1 flex items-center gap-3 p-3 md:p-4 active:bg-slate-50 transition text-left disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {/* AVATAR */}
                <div className="w-10 h-10 rounded-xl bg-[#E8F5E1] flex items-center justify-center shrink-0">
                  <span className="text-base font-semibold text-[#0A4730]">
                    {(r.nickname || r.full_name || "?")[0].toUpperCase()}
                  </span>
                </div>

                {/* INFO */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#0a2540] leading-tight truncate">
                    {r.nickname || r.full_name}
                  </p>
                  <p className="text-[10px] text-[#0a2540]/40 mt-0.5 truncate">
                    {r.full_name}
                    {r.country_code ? ` · ${r.country_code}` : ""}
                    {r.currency ? ` · ${r.currency}` : ""}
                  </p>
                </div>

                <ChevronRight size={15} className="text-[#0a2540]/20 shrink-0" />
              </button>

              {/* DIVIDER */}
              <div className="w-px h-8 bg-slate-100 shrink-0" />

              {/* EDIT */}
              <button
                onClick={() => navigate(`/editrecipient/${r.id}`)}
                className="px-4 h-full flex items-center justify-center active:bg-slate-50 transition"
              >
                <Pencil size={14} className="text-[#0a2540]/30" />
              </button>
            </div>
          ))}

        </div>
      </div>
    </div>
  );
}
